import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppCtx } from "../../../../AppCtx";
import { useSongContentService } from "../../hooks/useSongContentService";
import { SongsEmptySectionsRemover } from "../../services/songs-empty-sections-remover";
import { bracketAnnotation, isSectionAnnotation } from "../../services/song-header-metadata";
import "./PresentationSongsPageComponent.css";
import {
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiCloseLine,
  RiEyeLine,
  RiEyeOffLine,
  RiFileTextLine,
  RiHashtag,
  RiMusic2Line
} from "react-icons/ri";

const clamp = (value, min, max) => {
  if (Number.isNaN(value) || value === null || value === undefined) return min;
  return Math.max(min, Math.min(value, max));
};

// Same inner bracket text the reader prints for the section marker line.
const sectionMarkerLabel = (section) => {
  for (const group of section?.content ?? []) {
    for (const line of Array.isArray(group) ? group : [group]) {
      if (isSectionAnnotation(line)) return bracketAnnotation(line)[1].trim();
    }
  }
  return "";
};

const PresentationRemoteControlPanelComponent = () => {
  const {
    isPresentationControlPanelVisible,
    setPresentationControlPanelVisible,
    sharedPresentationState,
    publishPresentationState,
    selectedFile,
    arePresentationChordsVisible,
    isBlankScreen,
    isLyricsOnlyMode,
    isPresentationMode
  } = useContext(AppCtx);
  const direct = process.env.REACT_APP_HOSANNA_DIRECT === '1';
  const { songsJson } = useSongContentService(true, direct ? {name: sharedPresentationState?.fileName || selectedFile?.name} : undefined);

  const panelRef = useRef(null);
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const root = document.documentElement;
    const property = '--presentation-remote-panel-height';
    const previous = root.style.getPropertyValue(property);
    const measure = () => root.style.setProperty(property, `${panel.getBoundingClientRect().height}px`);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(panel);
    return () => {
      observer?.disconnect();
      if (previous) root.style.setProperty(property, previous);
      else root.style.removeProperty(property);
    };
  }, [isPresentationControlPanelVisible, isPresentationMode]);

  const [showNumberPad, setShowNumberPad] = useState(false);
  const [numberInput, setNumberInput] = useState("");

  useEffect(() => {
    if (!isPresentationControlPanelVisible) {
      setShowNumberPad(false);
      setNumberInput("");
    }
  }, [isPresentationControlPanelVisible]);


  const sharedFileName = sharedPresentationState?.fileName;
  const selectedFileName = selectedFile?.name;
  const activeFileName = sharedFileName || selectedFileName;
  const songs = useMemo(() => {
    if (!songsJson?.songs || !songsJson.songs.length) {
      return [];
    }

    if (!activeFileName || (!direct && activeFileName !== selectedFileName)) {
      return [];
    }

    return songsJson.songs;
  }, [songsJson?.songs, activeFileName, selectedFileName, direct]);

  const totalSongs = songs.length;

  const currentIndexes = useMemo(() => {
    if (!totalSongs) {
      return { songIndex: 0, sectionIndex: 0, totalSections: 0 };
    }

    const baseSongNumber = sharedPresentationState?.songNumber
      ?? (typeof selectedFile?.songNumber === "number" ? selectedFile.songNumber : parseInt(selectedFile?.songNumber ?? 1, 10))
      ?? 1;

    const songIndex = clamp(baseSongNumber - 1, 0, totalSongs - 1);
    const sections = songs[songIndex]?.sections ?? [];
    const totalSections = sections.length || 0;

    const lineSectionIndex = direct ? sections.findIndex(section => section.content.flat().some(line => line.lineNumber === sharedPresentationState?.line)) : -1;
    const rawSectionIndex = lineSectionIndex >= 0 ? lineSectionIndex : typeof sharedPresentationState?.sectionIndex === "number"
      ? sharedPresentationState.sectionIndex
      : (typeof sharedPresentationState?.sectionNumber === "number"
        ? sharedPresentationState.sectionNumber - 1
        : 0);

    const sectionIndex = clamp(rawSectionIndex, 0, Math.max(totalSections - 1, 0));

    return { songIndex, sectionIndex, totalSections };
  }, [sharedPresentationState, selectedFile?.songNumber, totalSongs, songs, direct]);

  const chordsVisibleState = sharedPresentationState?.chordsVisible;
  const blankScreenState = sharedPresentationState?.blankScreen;
  const lyricsOnlyModeState = sharedPresentationState?.lyricsOnlyMode;
  const currentChordsVisible = chordsVisibleState ?? arePresentationChordsVisible;
  const currentBlankScreen = blankScreenState ?? isBlankScreen;
  const currentLyricsOnlyMode = lyricsOnlyModeState ?? isLyricsOnlyMode;

  const publishAt = useCallback((songIndex, sectionIndex, overrides = {}) => {
    const fileName = sharedFileName || selectedFileName;
    const effectiveSongIndex = overrides.songIndex ?? songIndex;
    const effectiveSectionIndex = direct && overrides.songNumber === sharedPresentationState?.songNumber
      ? sectionIndex : overrides.sectionIndex ?? sectionIndex;

    if (!fileName || effectiveSongIndex < 0 || effectiveSectionIndex < 0) {
      return;
    }

    publishPresentationState({
      fileName,
      ...(direct ? {kind: overrides.kind ?? 'section'} : {}),
      songNumber: overrides.songNumber ?? (effectiveSongIndex + 1),
      sectionIndex: effectiveSectionIndex,
      sectionNumber: overrides.sectionNumber ?? (effectiveSectionIndex + 1),
      chordsVisible: overrides.chordsVisible ?? currentChordsVisible,
      blankScreen: overrides.blankScreen ?? currentBlankScreen,
      lyricsOnlyMode: overrides.lyricsOnlyMode ?? currentLyricsOnlyMode
    });
  }, [sharedFileName, selectedFileName, publishPresentationState, currentChordsVisible, currentBlankScreen, currentLyricsOnlyMode, direct, sharedPresentationState?.songNumber]);

  const handleNavigation = useCallback((direction) => {
    if (!songs.length) {
      return;
    }

    const { songIndex, sectionIndex } = currentIndexes;
    const effectiveShowChords = currentChordsVisible && !currentLyricsOnlyMode;

    if (direction === "next") {
      let nextSongIndex = songIndex;
      let nextSectionIndex = sectionIndex;

      while (nextSongIndex < songs.length) {
        const song = songs[nextSongIndex];
        const totalSections = song?.sections?.length || 0;

        nextSectionIndex++;
        if (nextSectionIndex >= totalSections) {
          nextSongIndex++;
          nextSectionIndex = 0;
          if (nextSongIndex >= songs.length) break;
        }

        const nextSection = songs[nextSongIndex].sections[nextSectionIndex];
        if (SongsEmptySectionsRemover.isVisibleSection(nextSection, effectiveShowChords)) {
          publishAt(nextSongIndex, nextSectionIndex);
          return;
        }
      }
    } else if (direction === "prev") {
      let prevSongIndex = songIndex;
      let prevSectionIndex = sectionIndex;

      while (prevSongIndex >= 0) {
        prevSectionIndex--;
        if (prevSectionIndex < 0) {
          prevSongIndex--;
          if (prevSongIndex < 0) break;
          prevSectionIndex = (songs[prevSongIndex]?.sections?.length || 1) - 1;
        }

        const prevSection = songs[prevSongIndex].sections[prevSectionIndex];
        if (SongsEmptySectionsRemover.isVisibleSection(prevSection, effectiveShowChords)) {
          publishAt(prevSongIndex, prevSectionIndex);
          return;
        }
      }
    }
  }, [currentIndexes, publishAt, songs, currentChordsVisible, currentLyricsOnlyMode]);

  useEffect(() => {
    if (!isPresentationControlPanelVisible || isPresentationMode) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.defaultPrevented) {
        return;
      }
      if (event.target.closest?.('input, textarea, select, [contenteditable="true"], .monaco-editor')) return;

      if (event.key === "ArrowRight") {
        event.preventDefault();
        handleNavigation("next");
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        handleNavigation("prev");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleNavigation, isPresentationControlPanelVisible, isPresentationMode]);

  const handleToggleNumberPad = useCallback(() => {
    setShowNumberPad(prev => !prev);
  }, []);

  const handleNumberInput = useCallback((digit) => {
    setNumberInput(prev => `${prev}${digit}`);
  }, []);

  useEffect(() => {
    if (!numberInput) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      const parsed = parseInt(numberInput, 10);
      if (!Number.isNaN(parsed) && parsed > 0 && parsed <= songs.length) {
        publishAt(parsed - 1, 0);
      }
      setNumberInput("");
      setShowNumberPad(false);
    }, 1500);

    return () => clearTimeout(timeout);
  }, [numberInput, publishAt, songs.length]);

  const remoteSongNumber = sharedPresentationState?.songNumber;
  const remoteSectionIndex = sharedPresentationState?.sectionIndex;
  const remoteSectionNumber = sharedPresentationState?.sectionNumber;

  const handleToggleChords = useCallback(() => {
    publishAt(currentIndexes.songIndex, currentIndexes.sectionIndex, {
      kind: direct ? sharedPresentationState?.kind : undefined,
      chordsVisible: !currentChordsVisible,
      songNumber: remoteSongNumber,
      songIndex: typeof remoteSongNumber === "number" ? remoteSongNumber - 1 : undefined,
      sectionIndex: typeof remoteSectionIndex === "number" ? remoteSectionIndex : (typeof remoteSectionNumber === "number" ? remoteSectionNumber - 1 : undefined),
      sectionNumber: remoteSectionNumber
    });
  }, [currentIndexes, currentChordsVisible, publishAt, remoteSongNumber, remoteSectionIndex, remoteSectionNumber, direct, sharedPresentationState?.kind]);

  const handleToggleBlankScreen = useCallback(() => {
    publishAt(currentIndexes.songIndex, currentIndexes.sectionIndex, {
      kind: direct ? sharedPresentationState?.kind : undefined,
      blankScreen: !currentBlankScreen,
      songNumber: remoteSongNumber,
      songIndex: typeof remoteSongNumber === "number" ? remoteSongNumber - 1 : undefined,
      sectionIndex: typeof remoteSectionIndex === "number" ? remoteSectionIndex : (typeof remoteSectionNumber === "number" ? remoteSectionNumber - 1 : undefined),
      sectionNumber: remoteSectionNumber
    });
  }, [currentIndexes, currentBlankScreen, publishAt, remoteSongNumber, remoteSectionIndex, remoteSectionNumber, direct, sharedPresentationState?.kind]);

  const handleToggleLyricsOnlyMode = useCallback(() => {
    publishAt(currentIndexes.songIndex, currentIndexes.sectionIndex, {
      lyricsOnlyMode: !currentLyricsOnlyMode,
      songNumber: remoteSongNumber,
      songIndex: typeof remoteSongNumber === "number" ? remoteSongNumber - 1 : undefined,
      sectionIndex: typeof remoteSectionIndex === "number" ? remoteSectionIndex : (typeof remoteSectionNumber === "number" ? remoteSectionNumber - 1 : undefined),
      sectionNumber: remoteSectionNumber
    });
  }, [currentIndexes, currentLyricsOnlyMode, publishAt, remoteSongNumber, remoteSectionIndex, remoteSectionNumber]);

  const handleClosePanel = useCallback(() => {
    setPresentationControlPanelVisible(false);
  }, [setPresentationControlPanelVisible]);

  if (!isPresentationControlPanelVisible || isPresentationMode) {
    return null;
  }

  const { songIndex, sectionIndex, totalSections } = currentIndexes;
  const displaySong = totalSongs ? songIndex + 1 : 0;
  const displaySection = totalSections ? sectionIndex + 1 : 0;

  const currentSong = songs[songIndex];
  const songTitle = typeof currentSong?.title === "string" ? currentSong.title.trim() : "";
  const sectionName = sectionMarkerLabel(currentSong?.sections?.[sectionIndex]);
  const sectionLabel = sectionName || `Sekcja ${displaySection}/${totalSections || 1}`;

  const isReady = songs.length > 0 && activeFileName;
  const chordsVisibleForUi = currentChordsVisible;
  const blankScreenForUi = currentBlankScreen;
  const lyricsOnlyModeForUi = currentLyricsOnlyMode;

  return (
    <div ref={panelRef} className={`presentation-control-panel presentation-control-panel--remote${blankScreenForUi ? " presentation-control-panel--blank" : ""}`} role="dialog" aria-label="Zdalne sterowanie prezentacją">
      {direct && <span className="presentation-control-role" title="To urządzenie steruje prezentacją">Pilot</span>}
      {direct && <button type="button" className="presentation-control-button"
        disabled={!selectedFile?.name} aria-label="Prezentuj wybraną pieśń"
        onClick={() => publishPresentationState({fileName: selectedFile.name,
          songNumber: Number(selectedFile.songNumber) || 1, kind: 'song', blankScreen: false})}>Prezentuj wybraną</button>}
      <span className="presentation-control-break" aria-hidden="true" />
      <button
        type="button"
        className="presentation-control-button icon-only"
        onClick={(e) => { e.stopPropagation(); handleNavigation("prev"); }}
        disabled={!isReady}
        title="Poprzedni slajd"
        aria-label="Poprzedni slajd"
      >
        <RiArrowLeftSLine aria-hidden="true" />
      </button>
      <div
        className="presentation-control-info"
        aria-label={isReady
          ? `Pieśń ${displaySong} z ${totalSongs || 1}, sekcja ${displaySection} z ${totalSections || 1}${songTitle ? `: ${songTitle}` : ""}`
          : undefined}
      >
        {isReady ? (
          <>
            {songTitle && <span className="presentation-control-title" title={songTitle}>{songTitle}</span>}
            <span className="presentation-control-position">{`Pieśń ${displaySong}/${totalSongs || 1} · ${sectionLabel}`}</span>
          </>
        ) : (
          isPresentationMode ? "Trwa inicjalizacja prezentacji…" : "Otwórz ten sam plik, aby sterować"
        )}
      </div>
      <button
        type="button"
        className="presentation-control-button icon-only"
        onClick={(e) => { e.stopPropagation(); handleNavigation("next"); }}
        disabled={!isReady}
        title="Następny slajd"
        aria-label="Następny slajd"
      >
        <RiArrowRightSLine aria-hidden="true" />
      </button>
      <button
        type="button"
        className="presentation-control-button icon-only"
        onClick={(e) => { e.stopPropagation(); handleToggleNumberPad(); }}
        disabled={!isReady}
        aria-label="Wybierz numer pieśni"
        title={showNumberPad ? "Zamknij wybór" : "Wybierz numer"}
        aria-pressed={showNumberPad}
      >
        {showNumberPad ? <RiCloseLine aria-hidden="true" /> : <RiHashtag aria-hidden="true" />}
      </button>
      <button
        type="button"
        className="presentation-control-button icon-only"
        onClick={(e) => { e.stopPropagation(); handleToggleChords(); }}
        aria-label={chordsVisibleForUi ? "Ukryj akordy" : "Pokaż akordy"}
        title={chordsVisibleForUi ? "Ukryj akordy" : "Pokaż akordy"}
        aria-pressed={chordsVisibleForUi}
      >
        <RiMusic2Line aria-hidden="true" />
      </button>
      <button
        type="button"
        className="presentation-control-button icon-only with-label"
        onClick={(e) => { e.stopPropagation(); handleToggleBlankScreen(); }}
        aria-label={blankScreenForUi ? "Wyłącz czarny ekran" : "Czarny ekran"}
        title={blankScreenForUi ? "Wyłącz czarny ekran" : "Czarny ekran"}
        aria-pressed={blankScreenForUi}
      >
        {blankScreenForUi
          ? <RiEyeLine aria-hidden="true" />
          : <RiEyeOffLine aria-hidden="true" />}
        <span className="presentation-control-button-text">{blankScreenForUi ? "Ekran wygaszony" : "Czarny ekran"}</span>
      </button>
      {!direct && <button
        type="button"
        className="presentation-control-button icon-only"
        onClick={(e) => { e.stopPropagation(); handleToggleLyricsOnlyMode(); }}
        aria-label={lyricsOnlyModeForUi ? "Wyłącz tryb tylko tekst" : "Tylko tekst"}
        title={lyricsOnlyModeForUi ? "Wyłącz tryb tylko tekst" : "Tylko tekst"}
        aria-pressed={lyricsOnlyModeForUi}
      >
        <RiFileTextLine aria-hidden="true" />
      </button>}
      <button
        type="button"
        className="presentation-control-button icon-only"
        onClick={(e) => { e.stopPropagation(); handleClosePanel(); }}
        aria-label="Zamknij panel sterowania"
        title="Zamknij panel sterowania"
      >
        <RiCloseLine aria-hidden="true" />
      </button>

      {direct && <span className="presentation-control-hint">Pilot pokazuje tylko prezentowaną pieśń; pozostałe są zwinięte. Kliknij tytuł, aby przejść do innej.</span>}
      {showNumberPad && (
        <div className="number-pad-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="number-pad-grid">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((num, idx) => (
              <div
                key={idx}
                onClick={(e) => { e.stopPropagation(); handleNumberInput(num.toString()); }}
                className="number-pad-button"
              >
                {num}
              </div>
            ))}
          </div>
          <div className="number-pad-controls">
            <div onClick={(e) => { e.stopPropagation(); setNumberInput(""); }} className="number-pad-control">Wyczyść</div>
            <div onClick={(e) => { e.stopPropagation(); setShowNumberPad(false); setNumberInput(""); }} className="number-pad-control">Zamknij</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PresentationRemoteControlPanelComponent;
